import { JsonObject } from '../app/json/jsonValue.js';
import { IData } from './iface/iData.js';
import { IClientData } from './iface/iClientData.js';
import { IAction } from './iface/iAction.js';
import { IHostActionResolver } from './iface/iHostActionResolver.js';
import { HostHandlerKind } from './iface/hostHandlerKind.js';
import { IHostHandlerResolver } from './iface/iHostHandlerResolver.js';
import type { ITransientStore } from './iface/iTransientStore.js';
import { GetParentJsonValueHandler } from './iface/getParentJsonValueHandler.js';

/**
 * Purpose: Presents a host-resolved Name as an IData so the loaders, which fetch a provider object from the
 * store, dispatch it exactly like a native handler.
 *
 * Responsibilities:
 * - Carry the claimed Name as typeName (operation tracking keys on it)
 * - Forward getData to the subscribed IHostHandlerResolver
 *
 * Holds no state and no knowledge of how the host produces the data.
 */
export class HostResolvedData extends IData {
    private readonly _resolver: IHostHandlerResolver;
    private readonly _typeName: string;

    constructor(resolver: IHostHandlerResolver, typeName: string) {
        super();
        this._resolver = resolver;
        this._typeName = typeName;
    }

    get typeName(): string {
        return this._typeName;
    }

    getData(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null = null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): JsonObject {
        return this._resolver.resolve(HostHandlerKind.Data, this._typeName, appSite, appView, appFile, componentName,
            componentDepth, resolvedAppSite, resolvedComponentName, paramJson, componentJson, transientStore,
            configJson, getParentJsonValue) ?? new JsonObject();
    }
}

/**
 * Purpose: Presents a host-resolved Name as an IClientData raw-data preparer. Same role as HostResolvedData for
 * the client-data family.
 */
export class HostResolvedClientData extends IClientData {
    private readonly _resolver: IHostHandlerResolver;
    private readonly _typeName: string;

    constructor(resolver: IHostHandlerResolver, typeName: string) {
        super();
        this._resolver = resolver;
        this._typeName = typeName;
    }

    get typeName(): string {
        return this._typeName;
    }

    getClientData(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null = null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): JsonObject {
        return this._resolver.resolve(HostHandlerKind.ClientData, this._typeName, appSite, appView, appFile, componentName,
            componentDepth, resolvedAppSite, resolvedComponentName, paramJson, componentJson, transientStore,
            configJson, getParentJsonValue) ?? new JsonObject();
    }
}

/**
 * Purpose: Presents a host-resolved Name as an IAction so getDynamicAction answers for a claimed
 * Name like a native action handler. The loaders themselves only check the Name exists (containsAction) and
 * dispatch by the bound Name through RegistryStore.invokeDynamicAction.
 */
export class HostResolvedAction extends IAction {
    private readonly _resolver: IHostActionResolver;
    private readonly _typeName: string;

    constructor(resolver: IHostActionResolver, typeName: string) {
        super();
        this._resolver = resolver;
        this._typeName = typeName;
    }

    get typeName(): string {
        return this._typeName;
    }

    doAction(
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        actionJson: JsonObject | null,
        getParentJsonValue: GetParentJsonValueHandler | null
    ): void {
        this._resolver.doAction(this._typeName, appSite, appView, appFile, componentName, resolvedAppSite,
            resolvedComponentName, paramJson, componentJson, transientStore, actionJson, getParentJsonValue);
    }
}
